import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, ShoppingCart, Search, Bell } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatCLP, imgSrc, cartCount } from "@/lib/tienda";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function Productos() {
  const navigate = useNavigate();
  const [data, setData] = useState(null); // {productos, categorias}
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("todas");
  const [nCart, setNCart] = useState(cartCount());

  useEffect(() => {
    const f = () => setNCart(cartCount());
    window.addEventListener("odf-cart", f);
    return () => window.removeEventListener("odf-cart", f);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/tienda/productos`).then((r) => setData(r.data)).catch(() => setData({ productos: [], categorias: [] }));
  }, []);

  const productos = (data?.productos || []).filter((p) =>
    (cat === "todas" || p.categoria === cat) &&
    (!q || `${p.nombre} ${p.descripcion_corta} ${p.sku}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="productos-page" className="bg-[#F8FAFC] min-h-[70vh]">
      {/* header tienda */}
      <section className="bg-[#0B1020] relative overflow-hidden">
        <div className="absolute inset-0 odf-grid opacity-[0.10]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10 py-14 md:py-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-3">Tienda Odontofix</p>
              <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white">
                Repuestos e insumos<br />con respaldo técnico
              </h1>
            </div>
            <Button className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white h-12 px-5 relative self-start sm:self-end"
              onClick={() => navigate("/productos/checkout")}>
              <ShoppingCart className="h-5 w-5 mr-2" /> Carrito
              {nCart > 0 && (
                <span className="absolute -top-2 -right-2 h-6 min-w-6 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                  {nCart}
                </span>
              )}
            </Button>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        {data === null ? (
          <p className="text-center text-gray-400 py-20">Cargando catálogo…</p>
        ) : data.productos.length === 0 ? (
          <div className="text-center py-20 max-w-lg mx-auto">
            <ShoppingBag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-[#111827] mb-2">Estamos cargando el catálogo</h2>
            <p className="text-gray-500 mb-6">Muy pronto podrás comprar repuestos e insumos directo desde aquí. Mientras tanto, cotiza lo que necesites por WhatsApp.</p>
            <a href="https://wa.me/56929894282?text=Hola%20Odontofix%2C%20quiero%20cotizar%20un%20repuesto."
              target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#1fb757] text-white h-12 px-6">
                <Bell className="h-4 w-4 mr-2" /> Cotizar por WhatsApp
              </Button>
            </a>
          </div>
        ) : (
          <>
            {/* filtros */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto…"
                  className="pl-9 h-11 bg-white no-uppercase" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {["todas", ...(data.categorias || [])].map((c) => (
                  <button key={c} onClick={() => setCat(c)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                      cat === c ? "bg-[#384091] text-white border-[#384091]"
                                : "bg-white text-gray-600 border-gray-200 hover:border-[#384091]/40"}`}>
                    {c === "todas" ? "Todas" : c}
                  </button>
                ))}
              </div>
            </div>

            {/* grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {productos.map((p) => (
                <Link key={p.id} to={`/productos/${p.slug}`}
                  className="group rounded-2xl bg-white border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all">
                  <div className="aspect-square bg-gray-50 overflow-hidden relative">
                    {p.imagenes?.[0] ? (
                      <img src={imgSrc(p.imagenes[0])} alt={p.nombre}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <ShoppingBag className="h-10 w-10" />
                      </div>
                    )}
                    {p.precio_oferta > 0 && p.precio_oferta < p.precio && (
                      <span className="absolute top-2 left-2 rounded-full bg-red-500 text-white text-[11px] font-bold px-2 py-0.5">OFERTA</span>
                    )}
                    {!p.disponible && (
                      <span className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm font-bold text-gray-500">AGOTADO</span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[13px] font-semibold text-[#111827] leading-snug line-clamp-2 min-h-[2.4em]">{p.nombre}</p>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span className="text-[15px] font-bold text-[#384091]">{formatCLP(p.precio_final)}</span>
                      {p.precio_oferta > 0 && p.precio_oferta < p.precio && (
                        <span className="text-xs text-gray-400 line-through">{formatCLP(p.precio)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {productos.length === 0 && <p className="text-center text-gray-400 py-14">Sin resultados para tu búsqueda</p>}
          </>
        )}
      </div>
    </div>
  );
}
